import {Action} from "./actions";
import {ButtonWidgetTypes, FontStyleType, FontWeightType, IconModel} from "./interfaces";
import {Animation} from "./animation";
import {Effect} from "./effect";
import {Scale} from "../geom/Scale";
import {WidgetActionModel} from "shared/xp/components/widgets/hoc/withAction";

type ObjectId = string;

export const enum WidgetTypes {
    LINE = "line",
    SHAPE = "shape",
    IMAGE = "image",
    LINK = "link",
    SCENE_NAVIGATOR = "scene-navigator",
    EMBED = "embed",
    YOUTUBE = "youtube",
    SOCIAL_SHARING = "social-sharing",
    TEXT = "text",
    BUTTON = "button",
    COMPOSITE = "composite",
    SALESFORCE_COMMERCE = "demandware" // Demandware acquired by Salesforce, it's now called Salesforce Commerce Cloud.
}

export const enum FontProviders {
    CUSTOM = "custom",
    GOOGLE = "google",
    STANDARD = "standard",
    TYPEKIT = "typekit",
    FONTSDOTCOM = "fontsDotCom"
}

export interface Sized {
    width: number;
    height: number;
}

export interface BaseWidget extends AnimatedWidget {
    type: WidgetTypes;
    top: number;
    left: number;
    rotation: number;
    layerIndex: number;
    scale: Scale;
    // This seems to be also of type `actions`, Might need to see if there's experience using this syntax or not.
    action?: Action;
    sceneNumber: string;
    onClickHandler: () => void;
    // Even though this is only used by a subset of widgets the core withDefaultWidgetBehavior uses it as a prop.
    widgetAction?: WidgetActionModel;
    cid?: string; // This is a unique (to the experience) identifier generated by Backbone and persisted.
}

export const DEFAULT_BASE_WIDGET: BaseWidget = {
    type: WidgetTypes.COMPOSITE, // This needs to be overridden by the implementation.
    top: 0,
    left: 0,
    rotation: 0,
    layerIndex: 0,
    scale: new Scale(),
    onClickHandler: () => undefined,
    trackerEventHandler: () => undefined,
    effects: [],
    animations: [],
    width: 0,
    height: 0,
    sceneNumber: "0",
};

export interface AnimatedWidget extends Sized {
    effects: ReadonlyArray<Effect>;
    animations: ReadonlyArray<Animation>;
    trackerEventHandler: (node: Element) => void;
}

export interface StyledWidget extends BaseWidget {
    radius?: string | number;
    borderWidth?: number;
    borderColor?: string;
    backgroundColor?: string;
    opacity?: number;
    boxShadow?: string;
}

interface LineWidget extends BaseWidget {
    type: WidgetTypes.LINE;
    lineColor: string;
    lineHeight: string; // number wrapped in a string
}
export type LineWidgetModel = Readonly<LineWidget>;

export interface ShapeWidget extends StyledWidget {
    type: WidgetTypes.SHAPE;
}
export type ShapeWidgetModel = Readonly<ShapeWidget>;

export enum sourceType {
    Scene7 = "Scene7Integration"
}
interface ImageWidget extends StyledWidget {
    type: WidgetTypes.IMAGE;
    imageId: ObjectId;
    src: string;
    sourceType?: sourceType;
    altText: string;
    originalImageWidth: number;
    originalImageHeight: number;
    borderRadius: number;
    supportsResize: boolean;
    isHidden: boolean;
    crop?: ImageCrop;
}
export interface ImageCrop {
    // Start coords
    x: number;
    y: number;
    // Crop rectangle size
    width: number;
    height: number;
}
export type ImageWidgetModel = Readonly<ImageWidget>;

interface ButtonWidget extends StyledWidget {
    type: WidgetTypes.BUTTON;
    icon: ReadonlyArray<IconModel>;
    label: string;
    borderColor: string;
    buttonType: ButtonWidgetTypes;
    cid: string;
    color: string;
    fontFamily: string;
    fontSize: number;
    fontStyle: FontStyleType;
    fontWeight: FontWeightType;
    fontProvider: string;
    fontMetadata: TypekitMetadata;
    layerIndex: number;
    opacity: number;
    padding: number;
}

export type ButtonWidgetModel = Readonly<ButtonWidget>;

interface LinkWidget extends BaseWidget {
    type: WidgetTypes.LINK;
}
export type LinkWidgetModel = Readonly<LinkWidget>;

interface SalesforceCommerceWidget extends BaseWidget {
    type: WidgetTypes.SALESFORCE_COMMERCE;
    productID: string;
}
export type SalesforceCommerceWidgetModel = Readonly<SalesforceCommerceWidget>;

interface SceneNavigatorWidget extends BaseWidget {
    type: WidgetTypes.SCENE_NAVIGATOR;
    direction: "horizontal" | "vertical";
    theme:
        | "white-1"
        | "white-2"
        | "white-3"
        | "white-4"
        | "white-5"
        | "black-1"
        | "black-2"
        | "black-3"
        | "black-4"
        | "black-5";
}
export type SceneNavigatorWidgetModel = Readonly<SceneNavigatorWidget>;

interface EmbedWidget extends BaseWidget {
    type: WidgetTypes.EMBED;
    url: string;
}
export type EmbedWidgetModel = Readonly<EmbedWidget>;

interface YoutubeWidget extends BaseWidget {
    type: WidgetTypes.YOUTUBE;
    videoID: string;
    autoplay: boolean;
    controls: boolean;
    loop: boolean;
    mute: boolean;
}
export type YoutubeWidgetModel = Readonly<YoutubeWidget>;

export enum SocialSharingWidgetStyleCategory {
    FlatSquared = "flat-squared",
    FlatRounded = "flat-rounded",
    Isolated = "isolated",
    FlatCircle = "flat-circle",
    WhiteCircle = "white-circle",
    BlackCircle = "black-circle"
}

export enum SocialSharingWidgetStyleName {
    Squared = "Squared",
    Rounded = "Rounded",
    Isolated = "Isolated",
    Circle = "Circle",
    White = "White",
    Black = "Black"
}

export enum SocialSharingWidgetNetwork {
    Facebook = "facebook",
    Twitter = "twitter",
    Linkedin = "linkedin",
    Pinterest = "pinterest",
    GooglePlus = "googleplus"
}

interface SocialSharingWidget extends BaseWidget {
    type: WidgetTypes.SOCIAL_SHARING;
    style: SocialSharingWidgetStyleCategory;
    url?: string;
    networks: ReadonlyArray<SocialSharingWidgetNetwork>;
    networkClickHandlers: Map<SocialSharingWidgetNetwork, () => void>;
}

export type SocialSharingWidgetModel = Readonly<SocialSharingWidget>;

export enum FontFormats {
    TrueType = "truetype",
    Woff = "woff",
    OpenType = "opentype"
}

export interface CustomFontMetadata {
    customFontId: string;
    format: FontFormats;
    path: string;
}

export interface FontsDotComMetadata {
    projectId: string;
}

export interface TypekitMetadata {
    kitID: string;
}

export interface GeneralFontFamily {
    fontProvider: FontProviders;
    fontMetadata?: CustomFontMetadata | FontsDotComMetadata | TypekitMetadata;
    fontFamily: string;
    fontStyle: string;
    fontWeight: string;
    cssFamilyNames: string;
    url?: string;
    target?: string;
}

interface CustomFontFamily extends GeneralFontFamily {
    fontProvider: FontProviders.CUSTOM;
    fontMetadata: CustomFontMetadata;
}
interface FontsDotComFontFamily extends GeneralFontFamily {
    fontProvider: FontProviders.FONTSDOTCOM;
    fontMetadata: FontsDotComMetadata;
}
interface TypekitFontFamily extends GeneralFontFamily {
    fontProvider: FontProviders.TYPEKIT;
    fontMetadata: TypekitMetadata;
}

interface StandardFontFamily extends GeneralFontFamily {
    fontProvider: FontProviders.STANDARD;
}

interface GoogleFontFamily extends GeneralFontFamily {
    fontProvider: FontProviders.GOOGLE;
}

export type FontFamily = CustomFontFamily | FontsDotComFontFamily | TypekitFontFamily | StandardFontFamily | GoogleFontFamily;

interface TextWidget extends BaseWidget {
    type: WidgetTypes.TEXT;
    color: string;
    disableCentering: true; // always true
    fontFamilies: FontFamily[];
    cssFamilyNames: string[];
    fontProvider: string;
    fontFamily: string;
    fontMetadata: TypekitMetadata;
    fontSize: number;
    fontStyle: string;
    fontVariant: string;
    fontWeight: string;
    layerIndex: number;
    letterSpacing: number;
    lineHeight: number;
    text: string;
    textAlign: string;
    textDecoration: string;
    textShadow: string;
    textTransform: string;
    isHidden: boolean;
}

export type TextWidgetModel = Readonly<TextWidget>;

interface CompositeWidget extends BaseWidget {
    type: WidgetTypes.COMPOSITE;
    group: ReadonlyArray<Widget>;
}

export type CompositeWidgetModel = Readonly<CompositeWidget>;

interface TrackedWidget {
    properties: any;
}

export type TrackedWidgetModel = Readonly<TrackedWidget>;

export type Widget = Readonly<
      LineWidget
    | ShapeWidget
    | ImageWidget
    | LinkWidget
    | SceneNavigatorWidget
    | EmbedWidget
    | YoutubeWidget
    | SocialSharingWidget
    | ButtonWidget
    | TextWidget
    | CompositeWidget
    | SalesforceCommerceWidget
>;



// WEBPACK FOOTER //
// ./src/shared/xp/models/widgets.ts